import { useState } from 'react';

import { Link } from '@tanstack/react-router';

import { Badge } from '@/components/ui/badge';
import { useWorkspace } from '@/features/workspaces';
import type { SignalView } from '@/lib/api/generated/model';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

import { useTriggerProfileSignals } from '../hooks/useTriggerProfileSignals';
import { SIGNAL_STATUSES, signalState } from '../signalStatus';
import { SignalPayloadDialog } from './SignalPayloadDialog';
import { SignalStatusFilter } from './SignalStatusFilter';
import { PendingRows } from '@/shared/components/PendingRows';
import { ReadFailure } from '@/shared/components/ReadFailure';

export interface SignalListProps {
  workspaceId: string;
  triggerProfileId: string;
}

/**
 * What arrived against this trigger profile (spec 090).
 *
 * A sibling of the run history, never merged into it. A run is what *fired*; a
 * signal is what *arrived*, and the whole reason this section exists is that the
 * two are not the same fact — a signal that produced no run is invisible in the
 * run list and is precisely the case an operator opens the profile to find.
 *
 * Four branches, like the run history beside it: pending, read-failed, empty, and
 * populated. The middle two are the ones that must not be confused. "This profile
 * has received nothing" and "we could not find out what it received" look
 * identical if a failed read is allowed to fall through to an empty list, and on
 * this surface that mistake is the entire bug being fixed (FR-009).
 */
export function SignalList({ workspaceId, triggerProfileId }: SignalListProps) {
  const { signals, total, hasMore, isPending, loadFailed, retry, isFetching } =
    useTriggerProfileSignals(workspaceId, triggerProfileId);
  const [status, setStatus] = useState<string | undefined>(undefined);
  /*
    Where a signal's task lives.

    A signal records the task's id and nothing else, and a task is only openable
    on its workflow's board — so the id alone cannot be turned into an address.
    The workspace aggregate is already in the cache for this workspace (the sheet
    beside this section reads it for workflow names), and it carries both halves:
    the task's `workflow_id`, and the workflow's `key`.

    A task the aggregate does not carry is *said* to be unreachable rather than
    linked to an address that would answer not-found (FR-006).
  */
  const { data: workspace } = useWorkspace(workspaceId);
  const boardKeyOf = (taskId: string): string | null => {
    const task = workspace?.tasks.find((candidate) => candidate.id === taskId);
    if (!task) return null;
    const workflow = workspace?.workflows.find(
      (candidate) => candidate.id === task.workflow_id,
    );
    return workflow?.key ?? null;
  };

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">Signals</h3>
      {isPending ? (
        <PendingRows label="Loading signals" />
      ) : loadFailed ? (
        <ReadFailure
          message="The signals for this trigger could not be read."
          onRetry={retry}
          isFetching={isFetching}
        />
      ) : !signals || signals.length === 0 ? (
        // Distinct from the failure above, in words rather than by absence — the
        // two states a reader must be able to tell apart on sight (SC-006).
        <p className="text-muted-foreground text-sm">
          Nothing has arrived for this trigger yet.
        </p>
      ) : (
        <Populated
          signals={signals}
          total={total}
          hasMore={hasMore}
          status={status}
          onStatusChange={setStatus}
          workspaceId={workspaceId}
          boardKeyOf={boardKeyOf}
        />
      )}
    </div>
  );
}

function Populated({
  signals,
  total,
  hasMore,
  status,
  onStatusChange,
  workspaceId,
  boardKeyOf,
}: {
  signals: SignalView[];
  total: number;
  hasMore: boolean;
  status: string | undefined;
  onStatusChange: (next: string | undefined) => void;
  workspaceId: string;
  boardKeyOf: (taskId: string) => string | null;
}) {
  // Counted over the whole page, not over what the filter left, so the pills keep
  // saying what is available rather than what is currently shown — a count that
  // changed as you filtered would make the filter impossible to reason about.
  const counts = countByStatus(signals);
  // Which payload is open, if any. One at a time, by construction: the dialog is
  // the deliberate act FR-017 asks for, and two of them would not be one.
  const [inspecting, setInspecting] = useState<SignalView | null>(null);
  // Narrowing only. The order is the service's, newest first, and it survives
  // every filter: "did my push arrive?" is a question about time, and a list that
  // reorders under a filter stops being a chronology (FR-007, FR-008).
  const shown =
    status === undefined
      ? signals
      : signals.filter((signal) => signal.status === status);

  return (
    <div className="flex flex-col gap-2">
      <SignalStatusFilter
        value={status}
        onChange={onStatusChange}
        counts={counts}
      />

      {/*
        The bound, admitted (FR-014). Only when the history actually runs past the
        page: a notice that is always on screen is a notice nobody reads, and this
        one has to be believed on the day it matters.
      */}
      {hasMore ? (
        <p className="text-muted-foreground text-xs">
          Showing the most recent {signals.length} of {total}.
        </p>
      ) : null}

      {shown.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No signals with that status.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {shown.map((signal) => (
            <SignalRow
              key={signal.id}
              signal={signal}
              workspaceId={workspaceId}
              boardKey={
                signal.related_task_id === null
                  ? null
                  : boardKeyOf(signal.related_task_id)
              }
              onInspect={() => {
                setInspecting(signal);
              }}
            />
          ))}
        </ul>
      )}

      <SignalPayloadDialog
        signal={inspecting}
        onClose={() => {
          setInspecting(null);
        }}
      />
    </div>
  );
}

function SignalRow({
  signal,
  workspaceId,
  boardKey,
  onInspect,
}: {
  signal: SignalView;
  workspaceId: string;
  /** The board the signal's task lives on, or `null` if it cannot be reached. */
  boardKey: string | null;
  onInspect: () => void;
}) {
  const state = signalState(signal.status);

  return (
    <li className="border-border flex flex-col gap-1 rounded-lg border px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        {/*
          The state as a word. A badge variant may carry the same meaning again in
          colour and may never be the only thing carrying it: three of these four
          states are an ordered progression and the fourth is a failure, which is
          exactly what cannot be ranked by hue (FR-003).
        */}
        <Badge
          variant={signal.status === 'failed' ? 'destructive' : 'secondary'}
          className="shrink-0"
        >
          {state.label}
        </Badge>
        <span
          className="text-muted-foreground shrink-0 text-xs tabular-nums"
          title={formatExactTime(signal.created_at)}
        >
          {formatRelativeTime(signal.created_at)}
        </span>
      </div>
      {/*
        What the external system called it. `break-all` because an external key is
        somebody else's identifier and can be arbitrarily long without a space in
        it — unwrapped, one of them stretches the sheet.
      */}
      <p className="text-muted-foreground text-xs break-all">
        {signal.external_key}
      </p>
      {/*
        The reason, where there is one (FR-004). Selectable text rather than a
        title attribute or a truncation with no way back to the rest: this is the
        single most useful thing the surface has, it is already recorded, and an
        operator's next move is usually to quote it to somebody.

        `break-words` and a scroll ceiling because the service does not bound this
        string. An unbounded error left to lay itself out pushes every row below it
        off the sheet, which loses the chronology to explain one entry in it.
      */}
      {signal.error ? (
        <p className="text-destructive max-h-32 overflow-y-auto text-xs break-words">
          {signal.error}
        </p>
      ) : null}
      {/*
        The task it became (FR-005), or the admission that it cannot be opened
        (FR-006). A link that answers not-found is worse than no link: it spends
        the reader's trust to tell them nothing.

        A signal with no task at all says nothing here. `done` does not promise a
        task — a signal can be processed to the decision that no task was
        warranted — and inventing a failure out of that would be this surface
        claiming something the service never said.
      */}
      {signal.related_task_id === null ? null : boardKey === null ? (
        <p className="text-muted-foreground text-xs">
          Its task can no longer be reached.
        </p>
      ) : (
        <Link
          to="/workspaces/$workspaceId/workflows/$workflowKey/board"
          params={{ workspaceId, workflowKey: boardKey }}
          search={{ task: signal.related_task_id, tab: 'details' as const }}
          className="text-xs underline underline-offset-2"
        >
          Open the task it created
        </Link>
      )}
      {/*
        The way to the payload, and the only one (FR-016, FR-017). Nothing about
        the body appears in the row itself — not truncated, not previewed, not in
        a `title` — because a payload glimpsed while scanning is a payload nobody
        chose to look at.
      */}
      <button
        type="button"
        onClick={onInspect}
        className="text-muted-foreground hover:text-foreground self-start text-xs underline underline-offset-2"
      >
        Inspect what arrived
      </button>
    </li>
  );
}

/** How many signals carry each status, for the filter's own counts. */
function countByStatus(signals: SignalView[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const status of SIGNAL_STATUSES) counts[status] = 0;
  for (const signal of signals) {
    counts[signal.status] = (counts[signal.status] ?? 0) + 1;
  }
  return counts;
}
